import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import apiClient, { API_BASE_URL } from '../../utils/apiClient';
import './TabContainer.css';

const TabContainer = ({ platform, controlsData, gameId, pageContext, additionalHint }) => {
  const [activeTab, setActiveTab] = useState('controls');
  const [credits, setCredits] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [gameData, setGameData] = useState(null);
  const navigate = useNavigate();
  
  // Refs to store scroll positions
  const scrollPositions = useRef({
    controls: 0,
    credits: 0
  });
  
  // Ref for the content container
  const contentRef = useRef(null);

  // Setup default credits based on emulator type
  useEffect(() => {
    if (!platform) return;

    // Only use credits from controlsData if they exist
    if (controlsData && controlsData.credits) {
      setCredits(controlsData.credits);
    } else {
      setCredits([]);
    }
  }, [platform, controlsData]);

  // Load favorites from localStorage
  useEffect(() => {
    const savedFavorites = localStorage.getItem('favorites');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);

  // Fetch game data to get creator information
  useEffect(() => {
    const fetchGameData = async () => {
      if (!gameId) return;
      
      try {
        const response = await apiClient.get(`/games/${gameId}`);
        setGameData(response.data);
      } catch (error) {
        console.error('Error fetching game data:', error);
      }
    };

    fetchGameData();
  }, [gameId]);

  // Check if a game is favorited
  const isFavorite = (gameId) => {
    return favorites.includes(parseInt(gameId));
  };

  // Toggle favorite status
  const toggleFavorite = (gameId) => {
    const gameIdInt = parseInt(gameId);
    let newFavorites;
    
    if (favorites.includes(gameIdInt)) {
      newFavorites = favorites.filter(id => id !== gameIdInt);
    } else {
      newFavorites = [...favorites, gameIdInt];
    }
    
    setFavorites(newFavorites);
    localStorage.setItem('favorites', JSON.stringify(newFavorites));
  };

  // Save scroll position when switching tabs
  const handleTabChange = (newTab) => {
    if (contentRef.current) {
      // Save current scroll position
      scrollPositions.current[activeTab] = contentRef.current.scrollTop;
    }
    setActiveTab(newTab);
  };

  // Restore scroll position when tab content renders
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = scrollPositions.current[activeTab];
    }
  }, [activeTab]);

  const renderControlsTab = () => {
    if (!platform) return <div>Loading...</div>;

    // If controlsData is not loaded yet, show loading
    if (!controlsData) {
      return <div className="loading-controls">加载控制信息中...</div>;
    }

    const emulator = platform.emulator;
    
    // Use the JSON data if available for the notices
    if (emulator === 'ruffle') {
      return (
        <div className="tab-scrollable-content">
          {additionalHint && (
            <div className="emulator-hint hint">
              {additionalHint}
            </div>
          )}
          {controlsData.notices.map((notice, index) => (
            <div key={index} className={`flash-controls-message ${notice.type}`}>
              {notice.text}
            </div>
          ))}
        </div>
      );
    } else if (emulator === 'parchment') {
      return (
        <div className="tab-scrollable-content">
          {additionalHint && (
            <div className="emulator-hint hint">
              {additionalHint}
            </div>
          )}
          {controlsData.notices.map((notice, index) => (
            <div key={index} className={`${notice.type === 'hint' ? 'emulator-hint' : 'flash-controls-message'} ${notice.type}`}>
              {notice.text}
            </div>
          ))}
        </div>
      );
    } else if (emulator === 'apple2ts') {
      return (
        <div className="tab-scrollable-content">
          {additionalHint && (
            <div className="emulator-hint hint">
              {additionalHint}
            </div>
          )}
          {controlsData.notices.map((notice, index) => (
            <div key={index} className={`${notice.type === 'hint' ? 'emulator-hint' : 'flash-controls-message'} ${notice.type}`}>
              {notice.text}
            </div>
          ))}
        </div>
      );
    } else if (emulator === 'html') {
      return (
        <div className="tab-scrollable-content">
          {additionalHint && (
            <div className="emulator-hint hint">
              {additionalHint}
            </div>
          )}
          {controlsData.notices.map((notice, index) => (
            <div key={index} className={`${notice.type === 'hint' ? 'emulator-hint' : 'flash-controls-message'} ${notice.type}`}>
              {notice.text}
            </div>
          ))}
          {controlsData.controls.player1 && controlsData.controls.player1.length > 0 && (
            <div className="player-mappings">
              <div className="player-section">
                <h3>操作说明</h3>
                <table>
                  <thead>
                    <tr>
                      <th>控制</th>
                      <th>说明</th>
                    </tr>
                  </thead>
                  <tbody>
                    {controlsData.controls.player1.map((mapping, index) => (
                      <tr key={index}>
                        <td>{mapping.key}</td>
                        <td>{mapping.action}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      );
    }

    // For other emulators (like EJS)
    // Check if there are controls for player 1
    const hasPlayer1Controls = controlsData.controls.player1 && controlsData.controls.player1.length > 0;
    // Check if there are controls for player 2
    const hasPlayer2Controls = controlsData.controls.player2 && controlsData.controls.player2.length > 0;

    return (
      <div className="tab-scrollable-content">
        {additionalHint && (
          <div className="emulator-hint hint">
            {additionalHint}
          </div>
        )}
        {(hasPlayer1Controls || hasPlayer2Controls) && (
          <div className="player-mappings">
            {hasPlayer1Controls && (
              <div className="player-section">
                <h3>玩家一</h3>
                <table>
                  <thead>
                    <tr>
                      <th>按键</th>
                      <th>功能</th>
                    </tr>
                  </thead>
                  <tbody>
                    {controlsData.controls.player1.map((mapping, index) => (
                      <tr key={index}>
                        <td>{mapping.key}</td>
                        <td>{mapping.action}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {hasPlayer2Controls && (
              <div className="player-section">
                <h3>玩家二</h3>
                <table>
                  <thead>
                    <tr>
                      <th>按键</th>
                      <th>功能</th>
                    </tr>
                  </thead>
                  <tbody>
                    {controlsData.controls.player2.map((mapping, index) => (
                      <tr key={index}>
                        <td>{mapping.key}</td>
                        <td>{mapping.action}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        
        {controlsData.notices.map((notice, index) => (
          <div key={index} className={`emulator-hint ${notice.type}`}>
            {notice.text}
          </div>
        ))}
      </div>
    );
  };

  const renderCreditsTab = () => {
    return (
      <div className="tab-scrollable-content">
        {gameData && gameData.creatorDisplayName && (
          <div className="creator-section">
            <h3>收录者</h3>
            <div className="creator-info">
              <span className="creator-name">{gameData.creatorDisplayName}</span>
            </div>
          </div>
        )}

        {credits.length > 0 && (
          <>
            <h3>自豪地采用</h3>
            {credits.map((credit, index) => (
              <div key={index} className="credits-section">
                {credit.name && <div className="parchment-title">{credit.name}</div>}
                {credit.logo && (
                  <img 
                    src={typeof credit.logo === 'string' ? 
                      (credit.logo.startsWith('http') ? 
                        credit.logo : 
                        credit.logo.startsWith('/') ? 
                          `${API_BASE_URL}${credit.logo}` : 
                          `${API_BASE_URL}/${credit.logo}`
                      ) : 
                      credit.logo} 
                    alt={credit.name || "Emulator"} 
                    className={`credits-logo ${credit.logoClass || ''}`} 
                  />
                )}
                <div className="credits-links">
                  {credit.website && (
                    <a href={credit.website} target="_blank" rel="noopener noreferrer" className="credits-button">
                      官方网站
                    </a>
                  )}
                  {credit.github && (
                    <a href={credit.github} target="_blank" rel="noopener noreferrer" className="credits-button">
                      GitHub
                    </a>
                  )}
                </div>
              </div>
            ))}
          </>
        )}

        <div className="support-section">
          <h3>支持Retro Game Museum的运营</h3>
          <Link to="/about" className="support-button">
            关于我们
          </Link>
        </div>
      </div>
    );
  };

  return (
    <div className="keyboard-mappings">
      <div className="tabs">
        <div 
          className={`tab ${activeTab === 'controls' ? 'active' : ''}`}
          onClick={() => handleTabChange('controls')}
        >
          键盘映射
        </div>
        <div 
          className={`tab ${activeTab === 'credits' ? 'active' : ''}`}
          onClick={() => handleTabChange('credits')}
        >
          关于
        </div>
      </div>

      <div className="tab-content-container">
        <div ref={contentRef} className={`tab-content ${activeTab === 'controls' ? 'with-button' : ''}`}>
          {activeTab === 'controls' ? renderControlsTab() : renderCreditsTab()}
        </div>
        
        {/* Favorite and Game Details Buttons - Fixed at bottom */}
        {activeTab === 'controls' && (
          <div className="fixed-bottom-button-container">
            {/* Favorite Button - only show when not used by Feed */}
            {pageContext !== 'feed' && (
              <button 
                className={`tab-favorite-button ${isFavorite(gameId) ? 'favorited' : ''}`}
                onClick={() => toggleFavorite(gameId)}
                title={isFavorite(gameId) ? '取消收藏' : '添加到收藏'}
              >
                <span className="favorite-icon">
                  {isFavorite(gameId) ? '★' : '☆'}
                </span>
                <span className="favorite-text">
                  {isFavorite(gameId) ? '已收藏' : '收藏'}
                </span>
              </button>
            )}
            <button 
              className="goto-details-button"
              onClick={() => navigate(`/game-details/${gameId}`)}
            >
              查看游戏详情
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

TabContainer.propTypes = {
  platform: PropTypes.object.isRequired,
  controlsData: PropTypes.object,
  gameId: PropTypes.string.isRequired,
  pageContext: PropTypes.string
};

export default TabContainer;